/**
 * Per-browser preferences stored in localStorage.
 *
 * NOTE: kept on the client deliberately. The server side stays minimal
 * ("cheap man in the middle") — no per-user settings to sync.
 */

const TZ_KEY = "brainzr.timezone";

function browserTz(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

export function getTimezone(): string {
  if (typeof window === "undefined") return "UTC";
  return localStorage.getItem(TZ_KEY) || browserTz();
}

export function setTimezone(tz: string) {
  if (typeof window === "undefined") return;
  if (!tz) {
    localStorage.removeItem(TZ_KEY);
  } else {
    localStorage.setItem(TZ_KEY, tz);
  }
}

export function listTimezones(): string[] {
  // Modern browsers expose the IANA list. Fallback to a sensible-ish short list.
  try {
    const fn = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf;
    // `supportedValuesOf` returns canonical IANA zones only, and that list does
    // NOT contain "UTC" — which is exactly what `browserTz()` returns for a
    // UTC machine and as its catch fallback. A <select> whose value matches no
    // <option> renders the first one instead, so Settings showed the effective
    // zone as "Africa/Abidjan" (alphabetically first) while the app was using
    // UTC. Both are +00:00, so the times stayed right and nothing looked wrong
    // until a DST boundary pulled them apart. Keep UTC selectable.
    if (typeof fn === "function") {
      const zones = fn.call(Intl, "timeZone");
      return zones.includes("UTC") ? zones : ["UTC", ...zones];
    }
  } catch {
    /* ignore */
  }
  return [
    "UTC",
    "Europe/London",
    "Europe/Madrid",
    "Europe/Paris",
    "Europe/Amsterdam",
    "Europe/Berlin",
    "Europe/Athens",
    "Africa/Cairo",
    "Asia/Dubai",
    "Asia/Karachi",
    "Asia/Kolkata",
    "Asia/Bangkok",
    "Asia/Singapore",
    "Asia/Tokyo",
    "Australia/Sydney",
    "America/New_York",
    "America/Chicago",
    "America/Los_Angeles",
    "America/Sao_Paulo",
  ];
}

export function fmtTimeIn(ms: number, tz?: string): string {
  return new Intl.DateTimeFormat([], {
    timeZone: tz || getTimezone(),
    hour: "2-digit",
    minute: "2-digit",
    // `hour12: false` selects the h24 cycle, which renders the midnight hour
    // as 24:00-24:59 — so a programme at 00:30 displayed as "24:30". h23 is
    // the 00-23 cycle actually wanted. Affected every catch-up and EPG time
    // in the midnight hour.
    hourCycle: "h23",
  }).format(new Date(ms));
}

export function fmtDayIn(ms: number, tz?: string): string {
  const d = new Date(ms);
  const opts: Intl.DateTimeFormatOptions = {
    timeZone: tz || getTimezone(),
    weekday: "short",
    month: "short",
    day: "numeric",
  };
  return new Intl.DateTimeFormat([], opts).format(d);
}

/** True if `aMs` and `bMs` fall on the same calendar day in the given tz. */
export function sameDayIn(aMs: number, bMs: number, tz?: string): boolean {
  const f = new Intl.DateTimeFormat("en-CA", {
    timeZone: tz || getTimezone(),
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  return f.format(new Date(aMs)) === f.format(new Date(bMs));
}
